import i18n, { InitOptions } from "i18next";
import LocizeBackend, { LocizeBackendOptions } from "i18next-locize-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import store from "store";

declare module "i18next" {
  interface CustomTypeOptions {
    returnNull: false;
  }
}

const rootTranslationsProjectId: string = import.meta.env
  .REACT_APP_ROOT_TRANSLATIONS_PROJECT_ID!;
const translationsProjectVersion: string | undefined = import.meta.env
  .REACT_APP_FT_TRANSLATIONS_VERSION;

const formatCurrency = (value: number, currency: string, lng?: string) => {
  return Intl.NumberFormat(lng, {
    style: "currency",
    currency: currency.trim(),
  }).format(value);
};

const formatNumber = (value: number, lng?: string) => {
  return new Intl.NumberFormat(lng).format(value);
};

const defaultLng = "en";

/**
 * This is the root configuration for Locize.
 *
 * The project id that is set here should be the default for the Payments Anywhere project.
 * The version should reflect the environment. This may be more future thinking but when a
 * release to production is made the translations should be versioned/branched so that any
 * changes to those translations in the "latest"/development version do not change the
 * displayed text in production (or another env.)
 */
const locizeOptions: LocizeBackendOptions = {
  projectId: rootTranslationsProjectId,
  version: translationsProjectVersion,
  referenceLng: defaultLng,
  crossDomain: true,
  cdnType: "pro",
};

/*
 * This is to format the translation with sepecfic type:
 *
 *  1. dateOnly
 *  2. dateTime
 *  3. currency
 *  4. number
 */
const translationFormat = function (value: any, format?: any, lng?: any) {
  var formatOptions: any = format?.split(",");
  var numberFormat = formatOptions[0];
  var currencyFormat = formatOptions[1];

  switch (numberFormat) {
    case "dateOnly":
      // Format date
      var newDate = Date.parse(value);
      if (newDate)
        return new Intl.DateTimeFormat(lng, {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        }).format(newDate);
      return "";
    case "dateTime":
      // Format datetime
      var newDate1 = Date.parse(value);
      if (newDate1)
        return new Intl.DateTimeFormat(lng, {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }).format(newDate1);
      return "";
    case "currency":
      return formatCurrency(value, currencyFormat, lng);
    case "number":
      return formatNumber(value, lng);
  }
  return value;
};

/**
 * This is the root i18next configuration.
 *
 * This configuration should be the same per-env.
 */
const initOptions: InitOptions<LocizeBackendOptions> = {
  // debug: true, //TODO: comment this on production.
  fallbackLng: defaultLng,
  returnNull: false,
  interpolation: {
    escapeValue: false,
    format: translationFormat,
  },
  react: {
    useSuspense: true,
  },
  backend: locizeOptions,
  supportedLngs: ["en", "es-US"],
  ns: ["app"],
};

/*
 * The current client id is tracked so that when the store loads the configuration
 * the subscription can compare the loaded value with the current to see if there
 * is any change. If there is a change then the value is set and the i18next
 * module is reinitialized to pull the translatiosn for the current client.
 */
let currentClientId: number | null = null;

/*
 * A subscription is created on the root store. The subscription fires whenever
 * there is a state change in the store. The currentClientId is used to check if
 * the client state has changed. When this occurrs the i18next module is
 * reloaded to pull the translations for the current client.
 */
store.subscribe(() => {
  const { config } = store.getState();
  if (config.client?.clientId && currentClientId !== config.client?.clientId) {
    if (currentClientId !== null) {
      console.warn("[i18n] Client changed, reinitializing translations", {
        from: currentClientId,
        to: config.client.clientId,
      });
    }

    currentClientId = config.client.clientId;
    if (!!config.client.translationsProjectId) {
      initOptions.backend!.projectId = config.client.translationsProjectId!;
    } else {
      initOptions.backend!.projectId = rootTranslationsProjectId;
    }

    // Use setTimeout to defer reinitialization and avoid synchronous Suspense
    setTimeout(() => {
      i18n.init(initOptions);
    }, 0);
  }
});

i18n.use(LocizeBackend).use(LanguageDetector).use(initReactI18next);

export default i18n;
